"use client";

import React from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

export type MessageInputProps = {
  messageFile: File | null;
  localMessage: string;
  onMessageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void; // onKeyPress yerine
  sendingMessage: boolean;
  onMessageFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMessageFile: () => void;
  onSendMessage: () => void;
  formatFileSize: (bytes: number) => string;
};

export const MessageInput = React.memo(
  React.forwardRef<HTMLInputElement | HTMLTextAreaElement, MessageInputProps>(
    (
      {
        messageFile,
        localMessage,
        onMessageChange,
        onKeyDown,
        sendingMessage,
        onMessageFileSelect,
        onRemoveMessageFile,
        onSendMessage,
        formatFileSize,
      },
      ref
    ) => {
      return (
        <Box
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "white",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          }}
        >
          {messageFile && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "success.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "success.200",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: "success.100",
                      borderRadius: "50%",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 16, color: "success.600" }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="success.800">
                      {messageFile.name}
                    </Typography>
                    <Typography variant="caption" color="success.600">
                      {formatFileSize(messageFile.size)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={onRemoveMessageFile}
                  sx={{
                    color: "error.main",
                    backgroundColor: "error.50",
                    "&:hover": { backgroundColor: "error.100" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-end" }}>
            <Tooltip title="Dosya Ekle">
              <Button
                variant="outlined"
                component="label"
                size="medium"
                sx={{
                  minWidth: "auto",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  borderColor: "grey.300",
                  "&:hover": { borderColor: "primary.main", backgroundColor: "primary.50" },
                }}
                disabled={!!messageFile}
              >
                <AttachFileIcon sx={{ fontSize: 20 }} />
                <input
                  type="file"
                  hidden
                  onChange={onMessageFileSelect}
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv"
                />
              </Button>
            </Tooltip>

            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Notunuzu buraya yazın..."
              value={localMessage}
              onChange={onMessageChange}
              onKeyDown={onKeyDown}
              disabled={sendingMessage}
              inputRef={ref} // 🔑 forwardRef burada input/textarea'ya bağlanıyor
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: "primary.main" },
                  "&.Mui-focused": {
                    borderColor: "primary.main",
                    boxShadow: "0 0 0 2px rgba(25,118,210,0.1)",
                  },
                  "& textarea": {
                    resize: "vertical",
                    minHeight: "40px!important",
                    maxHeight: "120px!important",
                  },
                },
              }}
            />

            <Tooltip title="Notu Gönder">
              <IconButton
                onClick={onSendMessage}
                disabled={sendingMessage || (!localMessage.trim() && !messageFile)}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
                  },
                  "&:disabled": { backgroundColor: "grey.300", color: "grey.500" },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {sendingMessage ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: "block", textAlign: "center", fontSize: "0.75rem" }}
          >
            💡 Talep için not bırakabilir veya istenilen bilgileri gönderebilirsiniz
          </Typography>
        </Box>
      );
    }
  )
);

// TS devtools'ta daha okunabilir olsun diye:
MessageInput.displayName = "MessageInput";
